import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { getCurrentUser, showSpinner, hideSpinner } from 'src/app/core/services/functions.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { CargueTPService } from '../services/cargue-tp.service';
import * as XLSX from 'xlsx';

@Component({
    selector: 'app-cargue-productos-tp',
    templateUrl: './cargue-productosTP.component.html',
    styleUrls: ['./cargue-productosTP.component.scss']
})
export class CargueProductosTPComponent implements OnInit {

    cargueForm!: FormGroup;

    politicas: any[] = [];

    file: File | null = null;
    fileName: string = '';
    isFileLoaded: boolean = false;
    inputFile!: HTMLInputElement;

    recordsToSend: number = 1000;

    parsedInfo: any[] = [];
    incorrectInfo: any[] = [];
    headers: string[] = [];

    company: string;
    usuarioId: string = 'SADMIN';
    isLoadingPoliticas: boolean = false;
    isDragging: boolean = false;

    constructor(
        private fb: FormBuilder,
        private helpers: HelpersService,
        private cargueTPService: CargueTPService
    ) { }

    ngOnInit(): void {
        this.initForm();

        const usuario = getCurrentUser();
        this.usuarioId = usuario?.usuario_aplicativo ?? 'SADMIN';
        this.company = usuario?.c_emp ?? 'JA';
        console.log('Empresa:', this.company);

        this.obtenerPoliticas();
    }

    initForm(): void {
        this.cargueForm = this.fb.group({
            politica: ['', Validators.required]
        });
    }

    async obtenerPoliticas(): Promise<void> {
        this.isLoadingPoliticas = true;

        try {
            const response = await this.cargueTPService.obtenerPoliticas(this.company);

            if (response?.success) {
                this.politicas = response.data || [];
            } else {
                this.helpers.notifications.warningMessage(
                    response?.message || 'No se encontraron políticas'
                );
            }
        } catch (error) {
            this.helpers.notifications.errorMessage('Error consultando políticas');
        } finally {
            this.isLoadingPoliticas = false;
        }
    }

    onSelectFile(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.inputFile = input;

        if (!input.files || input.files.length === 0) {
            return;
        }

        this.handleFile(input.files[0], input);
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;

        const files = event.dataTransfer?.files;

        if (!files || files.length === 0) {
            return;
        }

        this.handleFile(files[0], this.inputFile);
    }

    clearFile(input: HTMLInputElement): void {
        this.resetFile(input);
    }

    private handleFile(file: File, input: HTMLInputElement): void {
        const extension = file.name.split('.').pop()?.toLowerCase();

        if (extension !== 'xls' && extension !== 'xlsx') {
            this.helpers.notifications.errorMessage('Solo se permiten archivos Excel (.xls, .xlsx)');
            this.resetFile(input);
            return;
        }

        if (file.size === 0) {
            this.helpers.notifications.errorMessage('El archivo está vacío');
            this.resetFile(input);
            return;
        }

        this.file = file;
        this.fileName = file.name;

        this.leerYValidarExcel(file);
    }

    private leerYValidarExcel(file: File): void {
        const reader = new FileReader();

        reader.onload = (e: any) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                raw: false
            });

            if (!rows || rows.length <= 1) {
                this.helpers.notifications.errorMessage('El archivo no contiene registros para cargar');
                this.resetFile(this.inputFile);
                return;
            }

            this.parsedInfo = [];
            this.incorrectInfo = [];

            const headers = rows[0].map(header =>
                String(header || '').trim().toUpperCase()
            );

            this.headers = headers;

            const posiblesCedula = ['N_IDE', 'CEDULA'];
            const posiblesEstado = ['EST', 'ESTADO'];

            const indexCedula = headers.findIndex(header => posiblesCedula.includes(header));
            const indexEstado = headers.findIndex(header => posiblesEstado.includes(header));

            if (indexCedula === -1 || indexEstado === -1) {
                this.helpers.notifications.errorMessage(
                    'El archivo debe contener columnas: N_IDE o CEDULA, y EST o ESTADO'
                );
                this.resetFile(this.inputFile);
                return;
            }

            rows.slice(1).forEach((row, index) => {
                const fila = index + 2;
                const cedula = row[indexCedula]?.toString().trim();

                if (!cedula) {
                    this.incorrectInfo.push({
                        fila,
                        error: 'Cédula / N_IDE vacío'
                    });
                    return;
                }

                this.parsedInfo.push({
                    n_ide: cedula,
                    est: 'A'
                });
            });

            if (this.parsedInfo.length === 0) {
                this.helpers.notifications.errorMessage('No hay registros válidos para cargar');
                this.resetFile(this.inputFile);
                return;
            }

            this.isFileLoaded = true;

            this.helpers.notifications.successMessage(
                `Archivo validado correctamente. Registros válidos: ${this.parsedInfo.length}`
            );

            if (this.incorrectInfo.length > 0) {
                this.helpers.notifications.warningMessage(
                    `Se encontraron ${this.incorrectInfo.length} registros con error`
                );
            }
        };

        reader.readAsArrayBuffer(file);
    }

    async onSubmit(): Promise<void> {
        if (this.cargueForm.invalid) {
            this.helpers.notifications.warningMessage('Debe seleccionar una política');
            return;
        }

        if (!this.parsedInfo || this.parsedInfo.length === 0) {
            this.helpers.notifications.warningMessage('No hay registros válidos para cargar');
            return;
        }

        // 'politica' ahora es el objeto completo seleccionado ({ NOMBRE, CODIGO, NUMERAL })
        const politicaSeleccionada = this.cargueForm.get('politica')?.value;
        const codigoPolitica = politicaSeleccionada?.CODIGO;
        const numeralPolitica = politicaSeleccionada?.NUMERAL;

        if (!codigoPolitica || !numeralPolitica) {
            this.helpers.notifications.warningMessage('Debe seleccionar una política válida');
            return;
        }

        try {
            let totalInsertados = 0;
            const totalLotes = Math.ceil(this.parsedInfo.length / this.recordsToSend);
            let contador = 1;

            for (let i = 0; i < this.parsedInfo.length; i += this.recordsToSend) {
                const porcentaje = Math.round((contador / totalLotes) * 100);

                showSpinner(`Cargando Registros ${porcentaje} %...`);

                const lote = this.parsedInfo.slice(i, i + this.recordsToSend);

                const payload = {
                    company: this.company,
                    politica: codigoPolitica,
                    numeral: numeralPolitica,
                    usuario: this.usuarioId,
                    registros: lote
                };

                const resp = await this.cargueTPService.cargarClientesPilotos(payload);

                if (!resp?.success) {
                    hideSpinner();
                    this.helpers.notifications.errorMessage(
                        resp?.message || 'Error cargando registros'
                    );
                    return;
                }

                totalInsertados += resp?.data?.insertados || lote.length;
                contador++;
            }

            hideSpinner();

            this.helpers.notifications.successMessage(
                `Cargue exitoso: ${totalInsertados} registros procesados`
            );

            this.resetFile(this.inputFile);
            this.cargueForm.reset();
            this.parsedInfo = [];
            this.incorrectInfo = [];

        } catch (error) {
            hideSpinner();
            this.helpers.notifications.errorMessage('Error al cargar registros');
        }
    }

    private resetFile(input: HTMLInputElement): void {
        this.file = null;
        this.fileName = '';
        this.isFileLoaded = false;
        this.parsedInfo = [];
        this.incorrectInfo = [];

        if (input) {
            input.value = '';
        }
    }
}